// Render-only transform feeding the "who's booked" surfaces (Phase 16, WHO-01).
// Takes the shipped event participants[] (kids already inline) + the viewer's
// member id, and returns an ordered, annotated array for rendering: each kid is
// placed immediately after its guardian (parent-adjacent, D-05); kids whose
// guardian is not a player still land in the orphan branch (D-03). Never mutates
// the input array (preserves react-query cache integrity) and never reads or
// derives any private minor detail the payload omits (D-08 privacy guardrail).
export interface BookedAttendee {
  id: string
  display_name: string
  profile_image: string | null
  is_child: boolean
  guardian_member_id: string | null
  booking_id?: string
  follows?: boolean | null
  checked_in?: boolean
  // derived (render-only):
  isYou: boolean
  isYourKid: boolean
  guardianName: string | null
}

export const buildBookedList = (participants: any[], viewerMemberId: string | null): BookedAttendee[] => {
  const adults = participants.filter((p) => !p.is_child)
  const nameByMemberId = new Map<string, string>(adults.map((a) => [a.id, a.display_name]))
  const kidsByGuardian = new Map<string, any[]>()
  for (const p of participants) {
    if (p.is_child && p.guardian_member_id) {
      const arr = kidsByGuardian.get(p.guardian_member_id) ?? []
      arr.push(p)
      kidsByGuardian.set(p.guardian_member_id, arr)
    }
  }

  // Project ONLY known-safe fields — never spread the raw participant, so no
  // private minor detail (date_of_birth/age) can leak through even if a future
  // payload includes it (D-08 privacy guardrail enforced at this chokepoint).
  const annotate = (p: any): BookedAttendee => ({
    id: p.id,
    display_name: p.display_name,
    profile_image: p.profile_image ?? null,
    is_child: !!p.is_child,
    guardian_member_id: p.guardian_member_id ?? null,
    booking_id: p.booking_id,
    follows: p.follows,
    checked_in: p.checked_in,
    isYou: !p.is_child && !!viewerMemberId && p.id === viewerMemberId,
    isYourKid: p.is_child && !!viewerMemberId && p.guardian_member_id === viewerMemberId,
    guardianName: p.is_child ? (nameByMemberId.get(p.guardian_member_id) ?? null) : null,
  })

  const out: BookedAttendee[] = []
  const orphanGuardians = new Set(kidsByGuardian.keys())
  // adults first, each immediately followed by their kids (D-05 adjacency)
  for (const adult of adults) {
    out.push(annotate(adult))
    for (const kid of kidsByGuardian.get(adult.id) ?? []) out.push(annotate(kid))
    orphanGuardians.delete(adult.id)
  }
  // kid-only case (D-03): guardian not attending -> kids alone, still attributed
  for (const gid of orphanGuardians) {
    for (const kid of kidsByGuardian.get(gid) ?? []) out.push(annotate(kid))
  }

  return out
}

export default buildBookedList
